import { checkboxClasses, Components, Theme } from '@mui/material';
import { Props } from '../../components/checkbox/types';
import { CSSProperties } from 'react';
import { FigmaColorsType } from '../../shared-types/theme/palette';
import CONFIG from '../../components/checkbox/config';

export const SIZES: Record<NonNullable<Props['size']>, CSSProperties> = {
  small: {
    width: 16,
    height: 16,
  },
  medium: {
    width: 20,
    height: 20,
  },
};

type ReturnType = Record<'MuiCheckbox', Components['MuiCheckbox']>;

export default function Checkbox(theme: Theme): ReturnType {
  return {
    MuiCheckbox: {
      defaultProps: {
        disableFocusRipple: true,
        disableRipple: true,
        disableTouchRipple: true,
        size: CONFIG.DEFAULT_SIZE,
      },
      variants: [
        {
          props: {
            size: 'small',
          },
          style: {
            width: SIZES.small.width,
            height: SIZES.small.height,
          },
        },
        {
          props: {
            size: 'medium',
          },
          style: {
            width: SIZES.medium.width,
            height: SIZES.medium.height,
          },
        },
        {
          props: {
            color: 'primary',
          },
          style: createCheckboxStylesByColor({ color: 'primary', theme }),
        },
        {
          props: {
            color: 'error',
          },
          style: createCheckboxStylesByColor({ color: 'error', theme }),
        },
        {
          props: {
            color: 'success',
          },
          style: createCheckboxStylesByColor({ color: 'success', theme }),
        },
        {
          props: {
            color: 'warning',
          },
          style: createCheckboxStylesByColor({ color: 'warning', theme }),
        },
      ],
      styleOverrides: {
        root: {
          padding: 0,
          color: 'transparent',
          borderRadius: theme.spacing(1),
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${theme.palette['Colors/Foreground/fg-quinary (400)']}`,
          [`&.${checkboxClasses.checked}`]: {
            border: 'unset',
          },
        },
        indeterminate: {
          border: 'unset',
        },
      },
    },
  };
}

const colors: Record<
  NonNullable<Props['color']>,
  { focusRing: FigmaColorsType; bg: FigmaColorsType }
> = {
  primary: {
    bg: 'Colors/Background/bg-brand-solid',
    focusRing: 'Colors/Effects/Focus rings/focus-ring',
  },
  error: {
    bg: 'Colors/Background/bg-error-solid',
    focusRing: 'Colors/Effects/Focus rings/focus-ring-error',
  },
  success: {
    bg: 'Colors/Background/bg-success-solid',
    focusRing: 'Colors/Effects/Focus rings/focus-ring',
  },
  warning: {
    bg: 'Colors/Background/bg-warning-solid',
    focusRing: 'Colors/Effects/Focus rings/focus-ring',
  },
};

function createCheckboxStylesByColor({
  color = 'primary',
  theme,
}: {
  color: Props['color'];
  theme: Theme;
}) {
  const colorKey = colors[color];

  return {
    [`&.${checkboxClasses.checked}`]: {
      backgroundColor: theme.palette[colorKey.bg],
    },
    [`&.${checkboxClasses.indeterminate}`]: {
      backgroundColor: theme.palette[colorKey.bg],
    },
    ':focus-within': {
      outline: `2px solid ${theme.palette[colorKey.focusRing]}`,
      outlineOffset: '2px',
    },
  };
}
